import { z } from 'zod';
import { ArtifactKind, ExecutionMode, Platform } from '@hangar/shared';
import { str, nstr, nint } from './schemaPrimitives';
import { EnvironmentSummary, ProviderAccountSummary, Warning } from './schemasCommon';
import { DefinedIn } from './schemasEnv';

const BindingSubmit = z.object({ autoSubmit: z.boolean().default(false), track: str, releaseStatus: str.default('completed'), rolloutFraction: z.number().nullish(), changesNotSentForReview: z.boolean().default(false) });
export const ProfileEnvironmentBinding = z.object({ environmentId: str, environment: str.optional(), providerConfig: z.record(str, z.unknown()).optional(), defaultStoreAppId: nstr, submit: BindingSubmit.nullish() });
export type ProfileEnvironmentBinding = z.infer<typeof ProfileEnvironmentBinding>;
export const Profile = z.object({
  id: str,
  appId: str,
  slug: str,
  name: str,
  platform: Platform,
  providerAccountId: str,
  providerAccount: ProviderAccountSummary.optional(),
  executionModes: z.array(ExecutionMode),
  providerConfig: z.record(str, z.unknown()).default({}),
  artifactKind: ArtifactKind.catch('apk'),
  launchForm: z.record(str, z.unknown()).optional(),
  verification: z.record(str, z.unknown()).optional(),
  versionStrategy: z.record(str, z.unknown()).optional(),
  submitDefaults: z.record(str, z.unknown()).optional(),
  timeoutMinutes: z.number().default(120),
  definedIn: DefinedIn.catch('ui'),
  environments: z.array(ProfileEnvironmentBinding).default([]),
  createdAt: str,
  updatedAt: str,
  archivedAt: nstr,
  etag: str.optional(),
});
export type Profile = z.infer<typeof Profile>;
export const ProfileList = z.object({ data: z.array(Profile), nextCursor: z.string().nullable() });

export const Guardrail = z.object({ code: str, level: z.enum(['info', 'warning', 'block']).catch('info'), field: nstr, message: str });
export type Guardrail = z.infer<typeof Guardrail>;
export const LaunchForm = z.object({
  profileId: str,
  profileSlug: str,
  platforms: z.array(Platform),
  environment: EnvironmentSummary,
  schema: z.record(str, z.unknown()).default({}),
  uiSchema: z.record(str, z.unknown()).default({}),
  defaults: z.record(str, z.unknown()).default({}),
  guardrails: z.array(Guardrail).default([]),
  allowedTracks: z.array(str).optional(),
  allowedExecutionModes: z.array(ExecutionMode).optional(),
  /** Whether Hangar starts these builds itself (Bitrise provider_api, the EAS runner); false for CI-only profiles, which carry an `HGR-EXECUTION-MODE` block guardrail. Absent from older APIs: unknown. */
  startable: z.boolean().optional(),
  executionMode: ExecutionMode.optional().catch(undefined),
  /** Bitrise profiles: the app and workflow each platform runs. */
  bitrise: z.array(z.object({ platform: str, appSlug: str, workflowId: str, stack: nstr })).nullish(),
  requiresApproval: z.boolean().optional(),
  activeFreezeWindow: z.record(str, z.unknown()).nullish(),
  versionPreview: z.array(z.object({ platform: Platform, versionName: nstr, nextBuildNumber: nint })).optional(),
});
export type LaunchForm = z.infer<typeof LaunchForm>;

export const ConfigChange = z.object({
  action: z.enum(['create', 'update', 'delete', 'noop']).catch('noop'),
  resourceType: str,
  identifier: str,
  resourceId: nstr,
  diff: z.array(z.object({ path: str, from: z.unknown().optional(), to: z.unknown().optional() })).default([]),
});
export type ConfigChange = z.infer<typeof ConfigChange>;
export const ConfigSyncResult = z.object({
  dryRun: z.boolean(),
  applied: z.boolean(),
  source: z.object({ kind: str.optional(), ref: nstr, commitSha: nstr, path: str.optional() }).optional(),
  configSha: nstr,
  plan: z.array(ConfigChange).default([]),
  warnings: z.array(Warning).default([]),
  errors: z.array(z.object({ pointer: str, message: str, code: str.optional() })).default([]),
});
export type ConfigSyncResult = z.infer<typeof ConfigSyncResult>;
